import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, orderBy, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';

export interface Conversation {
  id: string;
  title: string;
  lastUpdated: Date;
  userId: string;
}

interface ChatContextType {
  conversations: Conversation[];
  currentConversationId: string | null;
  setCurrentConversationId: (id: string | null) => void;
  createNewConversation: () => Promise<string>;
  updateConversationTitle: (id: string, title: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  isLoading: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user conversations
  useEffect(() => {
    const fetchConversations = async () => {
      if (!user) {
        setConversations([]);
        setCurrentConversationId(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const conversationsRef = collection(db, 'conversations');
        const q = query(
          conversationsRef,
          where('userId', '==', user.uid),
          orderBy('lastUpdated', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const conversationsData: Conversation[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          conversationsData.push({
            id: doc.id,
            title: data.title,
            lastUpdated: data.lastUpdated.toDate(),
            userId: data.userId
          });
        });

        setConversations(conversationsData);
        
        // Set current conversation to the most recent one if none is selected
        if (conversationsData.length > 0 && !currentConversationId) {
          setCurrentConversationId(conversationsData[0].id);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setIsLoading(false);
      }
    };

    fetchConversations();
  }, [user, currentConversationId]);

  const createNewConversation = async (): Promise<string> => {
    if (!user) throw new Error('User must be logged in to create a conversation');

    try {
      const newConversation = {
        title: 'New Conversation',
        lastUpdated: new Date(),
        userId: user.uid
      };

      const docRef = await addDoc(collection(db, 'conversations'), newConversation);
      
      setConversations((prev) => [
        {
          id: docRef.id,
          ...newConversation
        },
        ...prev
      ]);
      
      setCurrentConversationId(docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating new conversation:', error);
      throw error;
    }
  };

  const updateConversationTitle = async (id: string, title: string): Promise<void> => {
    if (!user) throw new Error('User must be logged in to update a conversation');

    try {
      await updateDoc(doc(db, 'conversations', id), {
        title,
        lastUpdated: new Date()
      });

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === id
            ? { ...conv, title, lastUpdated: new Date() }
            : conv
        )
      );
    } catch (error) {
      console.error('Error updating conversation title:', error);
      throw error;
    }
  };

  const deleteConversation = async (id: string): Promise<void> => {
    if (!user) throw new Error('User must be logged in to delete a conversation');

    try {
      await deleteDoc(doc(db, 'conversations', id));

      setConversations((prev) => prev.filter((conv) => conv.id !== id));
      
      // If the deleted conversation was the current one, select the most recent one
      if (currentConversationId === id) {
        const remainingConversations = conversations.filter(c => c.id !== id);
        setCurrentConversationId(
          remainingConversations.length > 0 ? remainingConversations[0].id : null
        );
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  };

  return (
    <ChatContext.Provider
      value={{
        conversations,
        currentConversationId,
        setCurrentConversationId,
        createNewConversation,
        updateConversationTitle,
        deleteConversation,
        isLoading
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}; 